import { useEffect } from 'react';

import { BatchStatusChannel, useChannel, wsClient, type Channel } from '../index';

export interface BatchStatusUpdate {
  type: string;
  payload: { batch_transfer_id: string };
  timestamp: string;
}

const requests = new Map<string, { consumers: number; timer: ReturnType<typeof setInterval> }>();
const eventTypes = new Set([
  'current_status',
  'batch_created',
  'batch_validating',
  'batch_validated',
  'batch_validation_failed',
  'batch_processing',
  'batch_progress',
  'batch_completed',
  'batch_partially_completed',
  'batch_failed',
  'batch_cancelled',
  'batch_status_changed',
  'batch_error',
  'item_processed',
]);

export function isBatchStatusUpdate(value: unknown): value is BatchStatusUpdate {
  if (!value || typeof value !== 'object') return false;
  const event = value as Record<string, unknown>;
  if (
    typeof event.type !== 'string' ||
    !eventTypes.has(event.type) ||
    typeof event.timestamp !== 'string'
  )
    return false;
  if (!event.payload || typeof event.payload !== 'object') return false;
  return typeof (event.payload as Record<string, unknown>).batch_transfer_id === 'string';
}

/** Subscribes to the existing BE channel; status requests also cover missing broadcasts. */
export function useBatchStatusUpdates(
  id: string | undefined,
  handler: (update: BatchStatusUpdate) => void,
  enabled = true
): void {
  const channel: Channel = {
    ...BatchStatusChannel,
    params: id ? { batch_transfer_id: id } : undefined,
  };
  useChannel<unknown>(
    channel,
    value => {
      if (isBatchStatusUpdate(value) && value.payload.batch_transfer_id === id) handler(value);
    },
    enabled && Boolean(id)
  );

  useEffect(() => {
    if (!enabled || !id) return;
    const registered = requests.get(id);
    if (registered) registered.consumers += 1;
    else {
      const scopedChannel = { ...BatchStatusChannel, params: { batch_transfer_id: id } };
      const request = () => {
        if (wsClient.isConnected()) wsClient.perform(scopedChannel, 'request_status');
      };
      request();
      requests.set(id, { consumers: 1, timer: setInterval(request, 3000) });
    }
    return () => {
      const registeredRequest = requests.get(id);
      if (!registeredRequest) return;
      registeredRequest.consumers -= 1;
      if (!registeredRequest.consumers) {
        clearInterval(registeredRequest.timer);
        requests.delete(id);
      }
    };
  }, [id, enabled]);
}
